<template>
  <div :class="['login-layout', `theme-${theme}`, { 'dark-mode': isDark }]">

    <!-- Background image -->
    <div class="bg-image"></div>

    <!-- Overlay -->
    <div class="bg-overlay"></div>

    <!-- Login Card -->
    <div class="login-card">

      <!-- Logo -->
      <div class="card-logo">
        <img src="@/assets/images/login-logo.png" alt="Ogun State Seal" />
      </div>

      <!-- Title -->
      <h1 class="card-title">Planning And Budgeting System</h1>

      <!-- Error -->
      <div v-if="errorMessage" class="error-banner">
        {{ errorMessage }}
      </div>

      <!-- Form -->
      <form @submit.prevent="handleSubmit" class="card-form">

        <div class="form-group">
          <label for="email">Username</label>
          <input
            id="email"
            v-model="email"
            type="email"
            placeholder="Enter your Username"
            @input="clearErrorMessage"
            required
          />
        </div>

        <div class="form-group">
          <label for="password">Password</label>
          <div class="password-wrapper">
            <input
              id="password"
              v-model="password"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Enter your Password"
              @input="clearErrorMessage"
              required
            />
            <span class="toggle-password" @click="togglePasswordVisibility">
              <i :class="toggleIconClass"></i>
            </span>
          </div>
        </div>

        <div class="forgot-row">
          <router-link to="/forgot-password" class="forgot-link">Forgot password?</router-link>
        </div>

        <button type="submit" class="login-btn" :disabled="isLoading">
          <span v-if="isLoading">
            <i class="fas fa-spinner fa-spin"></i> Signing In...
          </span>
          <span v-else>Log in</span>
        </button>

      </form>

      <!-- Footer -->
      <p class="card-footer">© 2025-{{ currentYear }} Powered by Tinovo Technology Services.</p>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import Cookies from 'js-cookie'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user'
import { useTheme } from '@/composables/useTheme'
const { theme } = useTheme()
const isDark = ref(localStorage.getItem('theme') === 'dark')


onMounted(() => {
  document.documentElement.setAttribute('data-bs-theme', isDark.value ? 'dark' : 'light')
  document.title = 'Login'
})

const email = ref('')
const password = ref('')
const errorMessage = ref('')
const passwordVisible = ref(false)
const isLoading = ref(false)
const router = useRouter()
const userStore = useUserStore()
const currentYear = new Date().getFullYear()

const toggleIconClass = computed(() =>
  passwordVisible.value ? 'far fa-eye-slash' : 'far fa-eye'
)

function clearErrorMessage() {
  errorMessage.value = ''
}

function togglePasswordVisibility() {
  passwordVisible.value = !passwordVisible.value
}

async function handleSubmit() {
  isLoading.value = true
  errorMessage.value = ''
  const root_api_url = process.env.VUE_APP_API_ROOT_URL

  try {
    const result = await axios.post(`${root_api_url}/auth/authenticate`, {
      email: email.value,
      password: password.value,
    })

    if (result.status === 200) {
      Cookies.set('auth_token', result.data.token, { expires: 1, path: '/' })
      userStore.setToken(result.data.token)
      await userStore.fetchProfile()
      await userStore.fetchProfilePhoto()
      await userStore.fetchSignature()
      router.push({ name: 'Dashboard' })
    }
  } catch (err) {
    if (err?.response?.status === 417) {
      errorMessage.value = err.response.data.errorMessage
    } else if (err?.response?.status === 503) {
      errorMessage.value = err.response.data.error
    } else {
      errorMessage.value = 'Unexpected error occurred, kindly contact administrator'
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
/* ─── Layout ─────────────────────────────────────── */
.login-layout {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

/* ─── Background image ────────────────────────────── */
.bg-image {
  position: absolute;
  inset: 0;
  background-image: url('@/assets/images/login-image-removebg2.png');
  background-size: cover;
  background-position: center;
  z-index: 0;
}

.bg-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.18);
  z-index: 1;
}

/* ─── Card ────────────────────────────────────────── */
.login-card {
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 460px;
  background: rgba(255, 255, 255, 0.62);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-radius: 16px;
  padding: 2.5rem 2.75rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 8px 48px rgba(0, 0, 0, 0.18);
}

/* ─── Logo ────────────────────────────────────────── */
.card-logo {
  width: 80px;
  height: 80px;
  margin-bottom: 1rem;
}

.card-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* ─── Title ───────────────────────────────────────── */
.card-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1a1a2e;
  text-align: center;
  margin-bottom: 1.5rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
}

/* ─── Error ───────────────────────────────────────── */
.error-banner {
  width: 100%;
  background: #fff0f0;
  border: 1px solid #f5c6c6;
  color: #c0392b;
  font-size: 0.875rem;
  border-radius: 8px;
  padding: 0.6rem 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
}

/* ─── Form ────────────────────────────────────────── */
.card-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.form-group label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.4rem;
}

.form-group input {
  width: 100%;
  padding: 0.65rem 1rem;
  border: 1.5px solid #d0d5dd;
  border-radius: 8px;
  font-size: 0.95rem;
  color: #1a1a2e;
  background: rgba(255, 255, 255, 0.7);
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.form-group input::placeholder {
  color: #a0aab4;
}

.form-group input:focus {
  border-color: var(--btn-alt);
  background: #fff;
}

/* ─── Password wrapper ───────────────────────────── */
.password-wrapper {
  position: relative;
}

.password-wrapper input {
  width: 100%;
  padding-right: 2.8rem;
  box-sizing: border-box;
}

.toggle-password {
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #7f8c8d;
  font-size: 0.95rem;
  line-height: 1;
}

.toggle-password:hover {
  color: #2c3e50;
}

/* ─── Forgot ──────────────────────────────────────── */
.forgot-row {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.4rem;
  margin-top: -0.25rem;
}

.forgot-link {
  font-size: 0.85rem;
  color: var(--btn-primary);
  text-decoration: none;
  font-weight: 500;
}

.forgot-link:hover {
  text-decoration: underline;
}

/* ─── Button ──────────────────────────────────────── */
.login-btn {
  width: 100%;
  padding: 0.75rem;
  background: var(--btn-primary);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.2s, transform 0.1s;
}

.login-btn:hover:not(:disabled) {
  background: var(--btn-primary-hover);
}

.login-btn:active:not(:disabled) {
  transform: scale(0.99);
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* ─── Footer ──────────────────────────────────────── */
.card-footer {
  margin-top: 1.5rem;
  font-size: 0.78rem;
  color: #7f8c8d;
  text-align: center;
}

/* ─── Dark mode ───────────────────────────────────── */
.dark-mode .login-card {
  background: rgba(20, 24, 36, 0.65);
}

.dark-mode .card-title {
  color: #f0f4f8;
}

.dark-mode .form-group label {
  color: #cdd5df;
}

.dark-mode .form-group input {
  background: rgba(255, 255, 255, 0.08);
  border-color: #3a4556;
  color: #f0f4f8;
}

.dark-mode .form-group input:focus {
  border-color: #27ae60;
  background: rgba(255, 255, 255, 0.12);
}

.dark-mode .card-footer {
  color: #6b7a8d;
}

/* ─── Responsive ──────────────────────────────────── */
@media (max-width: 520px) {
  .login-card {
    margin: 1rem;
    padding: 2rem 1.5rem 1.5rem;
  }

  .card-title {
    font-size: 1.1rem;
  }
}
</style>
